import React, { useState, useEffect, useContext } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import axios from "../../axiosConfig";
import TrainerLayout from "./TrainerLayout";
import { TrainerContext } from "../../context/TrainerContext";
import {
    FiEdit2,
    FiSave,
    FiX,
    FiPlus,
    FiChevronLeft,
    FiUsers,
    FiSearch,
    FiArrowLeft,
    FiCalendar
} from "react-icons/fi";
import DeleteConfirmationModal from "./DeleteGroupTrainingModal";
import GroupTrainings from "./GroupTrainings";
import "../../styles/TrainerGroupDetails.css"

const TrainerGroupDetails = () => {
    const { t } = useTranslation('groups');
    const { groupId } = useParams();
    const { trainer } = useContext(TrainerContext);
    const navigate = useNavigate();

    // Fetched group details
    const [groupDetails, setGroupDetails] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    // Edit mode state
    const [editMode, setEditMode] = useState(false);
    const [editedName, setEditedName] = useState("");
    const [editedDescription, setEditedDescription] = useState("");
    const [editedTrainerIds, setEditedTrainerIds] = useState([]);
    const [editedDogIds, setEditedDogIds] = useState([]);

    // Modal state for adding trainers/dogs
    const [isTrainerModalOpen, setIsTrainerModalOpen] = useState(false);
    const [isDogModalOpen, setIsDogModalOpen] = useState(false);
    const [modalTrainerSearch, setModalTrainerSearch] = useState("");
    const [modalDogSearch, setModalDogSearch] = useState("");
    const [allTrainers, setAllTrainers] = useState([]);
    const [allDogs, setAllDogs] = useState([]);

    // Delete confirmation modal state
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    // Fetch group details when component mounts
    useEffect(() => {
        const fetchGroupDetails = async () => {
            try {
                const token = localStorage.getItem("jwt");
                const response = await axios.get(`/api/v1/groups/${groupId}`, {
                    headers: { Authorization: `Bearer ${token}` },
                });

                setGroupDetails(response.data);
                setEditedName(response.data.name);
                setEditedDescription(response.data.description);
                setEditedTrainerIds(
                    response.data.groupTrainers ? response.data.groupTrainers.map(t => t.id) : []
                );
                setEditedDogIds(
                    response.data.groupDogs ? response.data.groupDogs.map(d => d.id) : []
                );
                setLoading(false);
            } catch (err) {
                setError(t('fetchDetailsError'));
                setLoading(false);
            }
        };

        fetchGroupDetails();
    }, [groupId]);

    // Open modal to add trainers: fetch all trainers for the school
    const openTrainerModal = async () => {
        try {
            const token = localStorage.getItem("jwt");
            const response = await axios.get(`/api/v1/trainer/find-all/${trainer.schoolId}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setAllTrainers(response.data || []);
            setIsTrainerModalOpen(true);
        } catch (err) {
            showNotification(t('fetchTrainersError'), "error");
        }
    };

    // Open modal to add dogs: fetch dogs for booking for the school
    const openDogModal = async () => {
        try {
            const token = localStorage.getItem("jwt");
            const response = await axios.get(`/api/v1/dog/list/${trainer.schoolId}/booking`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setAllDogs(response.data || []);
            setIsDogModalOpen(true);
        } catch (err) {
            showNotification(t('fetchDogsError'), "error");
        }
    };

    // Helper to show notifications
    const showNotification = (message, type = "success") => {
        const notification = document.createElement("div");
        notification.className = type === "success" ? "success-notification" : "error-notification";
        notification.textContent = message;
        document.body.appendChild(notification);
        setTimeout(() => {
            document.body.removeChild(notification);
        }, 3000);
    };

    // Filter modal trainers by search term
    const filteredModalTrainers = allTrainers.filter((t) => {
        const term = modalTrainerSearch.toLowerCase();
        const fullName = `${t.firstName} ${t.lastName}`.toLowerCase();
        return fullName.includes(term);
    });

    // Filter modal dogs by search term
    const filteredModalDogs = allDogs.filter((d) => {
        const term = modalDogSearch.toLowerCase();
        const info = `${d.name} ${d.breed} ${d.ownersName}`.toLowerCase();
        return info.includes(term);
    });

    // Save changes: compute differences and call multiple endpoints
    const handleSaveEdits = async () => {
        if (!editedName.trim()) {
            showNotification(t('groupNameRequired'), "error");
            return;
        }

        try {
            const token = localStorage.getItem("jwt");
            const headers = { Authorization: `Bearer ${token}` };

            // Calculate diffs
            const initialTrainerIds = groupDetails.groupTrainers
                ? groupDetails.groupTrainers.map(t => t.id)
                : [];
            const initialDogIds = groupDetails.groupDogs
                ? groupDetails.groupDogs.map(d => d.id)
                : [];

            const trainersToAdd = editedTrainerIds.filter(id => !initialTrainerIds.includes(id));
            const trainersToRemove = initialTrainerIds.filter(id => !editedTrainerIds.includes(id));
            const dogsToAdd = editedDogIds.filter(id => !initialDogIds.includes(id));
            const dogsToRemove = initialDogIds.filter(id => !editedDogIds.includes(id));

            // Update group name and description
            await axios.put(`/api/v1/groups/${groupId}`, {
                name: editedName,
                description: editedDescription,
            }, { headers });

            // Add new trainers
            if (trainersToAdd.length > 0) {
                await axios.post(`/api/v1/groups/${groupId}/trainers`, {
                    memberIds: trainersToAdd
                }, { headers });
            }

            // Remove trainers
            for (const trainerId of trainersToRemove) {
                await axios.delete(`/api/v1/groups/${groupId}/trainers/${trainerId}`, { headers });
            }

            // Add new dogs
            if (dogsToAdd.length > 0) {
                await axios.post(`/api/v1/groups/${groupId}/dogs`, {
                    memberIds: dogsToAdd
                }, { headers });
            }

            // Remove dogs
            for (const dogId of dogsToRemove) {
                await axios.delete(`/api/v1/groups/${groupId}/dogs/${dogId}`, { headers });
            }

            // Refresh group details
            const response = await axios.get(`/api/v1/groups/${groupId}`, { headers });
            setGroupDetails(response.data);
            setEditMode(false);
            showNotification(t('changesSaved'));
        } catch (err) {
            const errorMsg = err.response?.data?.message || t('saveError');
            showNotification(errorMsg, "error");
        }
    };

    // Handle deleting the entire group
    const handleDeleteGroup = async () => {
        setIsDeleting(true);

        try {
            const token = localStorage.getItem("jwt");
            await axios.delete(`/api/v1/groups/${groupId}`, {
                headers: { Authorization: `Bearer ${token}` },
            });

            showNotification(t('groupDeleted'));
            navigate("/trainer/groups");
        } catch (err) {
            const errorMsg = err.response?.data?.message || t('deleteError');
            showNotification(errorMsg, "error");
            setIsDeleting(false);
            setIsDeleteModalOpen(false);
        }
    };

    // Cancel edit mode
    const cancelEdit = () => {
        setEditedName(groupDetails.name);
        setEditedDescription(groupDetails.description);
        setEditedTrainerIds(groupDetails.groupTrainers ? groupDetails.groupTrainers.map(t => t.id) : []);
        setEditedDogIds(groupDetails.groupDogs ? groupDetails.groupDogs.map(d => d.id) : []);
        setEditMode(false);
    };

    if (loading) {
        return (
            <TrainerLayout>
                <div className="modern-loading">
                    <div className="modern-spinner"></div>
                    <p>{t('loading')}</p>
                </div>
            </TrainerLayout>
        );
    }

    if (error) {
        return (
            <TrainerLayout>
                <div className="modern-error">{error}</div>
            </TrainerLayout>
        );
    }

    if (!groupDetails) return null;

    return (
        <TrainerLayout>
            <div className="modern-group-details">
                <div className="modern-details-header">
                    <button
                        className="modern-back-button"
                        onClick={() => navigate("/trainer/groups")}
                    >
                        <FiArrowLeft />
                        <span>{t('backToGroups')}</span>
                    </button>

                    {!editMode ? (
                        <div className="modern-actions">
                            <button
                                className="modern-edit-button"
                                onClick={() => setEditMode(true)}
                            >
                                <FiEdit2 className="btn-icon" />
                                <span>{t('edit')}</span>
                            </button>
                            <button
                                className="modern-delete-button"
                                onClick={() => setIsDeleteModalOpen(true)}
                            >
                                <FiX className="btn-icon" />
                                <span>{t('delete')}</span>
                            </button>
                        </div>
                    ) : (
                        <div className="modern-actions">
                            <button
                                className="modern-cancel-button"
                                onClick={cancelEdit}
                            >
                                <FiX className="btn-icon" />
                                <span>{t('cancel')}</span>
                            </button>
                            <button
                                className="modern-save-button"
                                onClick={handleSaveEdits}
                            >
                                <FiSave className="btn-icon" />
                                <span>{t('save')}</span>
                            </button>
                        </div>
                    )}
                </div>

                <div className="modern-details-content">
                    {/* GROUP DETAILS SECTION */}
                    {!editMode && (
                        <>
                            <div className="modern-details-section">
                                <h2 className="modern-group-name">{groupDetails.name}</h2>
                                <p className="modern-group-description">
                                    {groupDetails.description || t('noDescription')}
                                </p>
                            </div>

                            <div className="modern-details-section">
                                <h3 className="modern-section-title">
                                    <FiUsers className="section-icon" />
                                    <span>{t('trainers')}</span>
                                    <span className="modern-count">
                                        {groupDetails.groupTrainers?.length || 0}
                                    </span>
                                </h3>

                                {groupDetails.groupTrainers && groupDetails.groupTrainers.length > 0 ? (
                                    <div className="modern-members-list">
                                        {groupDetails.groupTrainers.map((trainer) => (
                                            <div key={trainer.id} className="modern-member-card">
                                                <div className="modern-avatar">
                                                    {trainer.firstName.charAt(0)}
                                                    {trainer.lastName.charAt(0)}
                                                </div>
                                                <div className="modern-member-info">
                                                    <span className="modern-member-name">
                                                        {trainer.firstName} {trainer.lastName}
                                                    </span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="modern-empty-list">
                                        {t('noTrainersInGroup')}
                                    </div>
                                )}
                            </div>

                            <div className="modern-details-section">
                                <h3 className="modern-section-title">
                                    <FiUsers className="section-icon" />
                                    <span>{t('dogs')}</span>
                                    <span className="modern-count">
                                        {groupDetails.groupDogs?.length || 0}
                                    </span>
                                </h3>

                                {groupDetails.groupDogs && groupDetails.groupDogs.length > 0 ? (
                                    <div className="modern-members-list">
                                        {groupDetails.groupDogs.map((dog) => (
                                            <div key={dog.id} className="modern-member-card">
                                                <div className="modern-avatar dog-avatar">
                                                    {dog.name.charAt(0)}
                                                </div>
                                                <div className="modern-member-info">
                                                    <span className="modern-member-name">{dog.name}</span>
                                                    <span className="modern-member-details">
                                                        {dog.breed && <span>{dog.breed}</span>}
                                                        {dog.ownersName && <span>{t('caretaker')}: {dog.ownersName}</span>}
                                                    </span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="modern-empty-list">
                                        {t('noDogsInGroup')}
                                    </div>
                                )}
                            </div>
                        </>
                    )}

                    {/* EDIT MODE CONTENT */}
                    {editMode && (
                        <>
                            <div className="modern-edit-form">
                                <div className="modern-input-group">
                                    <label htmlFor="editName">{t('groupName')}</label>
                                    <input
                                        id="editName"
                                        type="text"
                                        value={editedName}
                                        onChange={(e) => setEditedName(e.target.value)}
                                        placeholder={t('groupNamePlaceholder')}
                                    />
                                </div>

                                <div className="modern-input-group">
                                    <label htmlFor="editDesc">{t('groupDescription')}</label>
                                    <textarea
                                        id="editDesc"
                                        value={editedDescription}
                                        onChange={(e) => setEditedDescription(e.target.value)}
                                        placeholder={t('groupDescriptionPlaceholder')}
                                        rows="3"
                                    />
                                </div>
                            </div>

                            <div className="modern-edit-section">
                                <div className="modern-section-header">
                                    <h3>{t('trainers')}</h3>
                                    <button
                                        className="modern-add-button"
                                        onClick={openTrainerModal}
                                    >
                                        <FiPlus className="btn-icon" />
                                        <span>{t('addTrainer')}</span>
                                    </button>
                                </div>

                                {editedTrainerIds.length > 0 ? (
                                    <div className="modern-edit-list">
                                        {editedTrainerIds.map(id => {
                                            // Try to find trainer in groupDetails first, then in allTrainers
                                            let trainer = groupDetails.groupTrainers?.find(t => t.id === id);
                                            if (!trainer) {
                                                trainer = allTrainers.find(t => t.id === id);
                                            }

                                            return (
                                                <div key={id} className="modern-edit-item">
                                                    <div className="modern-item-info">
                                                        <div className="modern-avatar small">
                                                            {trainer?.firstName?.charAt(0) || '?'}
                                                            {trainer?.lastName?.charAt(0) || '?'}
                                                        </div>
                                                        <span>
                                                            {trainer
                                                                ? `${trainer.firstName} ${trainer.lastName}`
                                                                : t('trainerFallback', { id })}
                                                        </span>
                                                    </div>
                                                    <button
                                                        className="modern-remove-btn"
                                                        onClick={() => setEditedTrainerIds(
                                                            editedTrainerIds.filter(trainerId => trainerId !== id)
                                                        )}
                                                    >
                                                        <FiX />
                                                    </button>
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <div className="modern-empty-edit-list">
                                        {t('noSelectedTrainers')}
                                    </div>
                                )}
                            </div>

                            <div className="modern-edit-section">
                                <div className="modern-section-header">
                                    <h3>{t('pets')}</h3>
                                    <button
                                        className="modern-add-button"
                                        onClick={openDogModal}
                                    >
                                        <FiPlus className="btn-icon" />
                                        <span>{t('addPet')}</span>
                                    </button>
                                </div>

                                {editedDogIds.length > 0 ? (
                                    <div className="modern-edit-list">
                                        {editedDogIds.map(id => {
                                            // Try to find dog in groupDetails first, then in allDogs
                                            let dog = groupDetails.groupDogs?.find(d => d.id === id);
                                            if (!dog) {
                                                dog = allDogs.find(d => d.id === id);
                                            }

                                            return (
                                                <div key={id} className="modern-edit-item">
                                                    <div className="modern-item-info">
                                                        <div className="modern-avatar small dog-avatar">
                                                            {dog?.name?.charAt(0) || '?'}
                                                        </div>
                                                        <div>
                                                            <span className="item-name">
                                                                {dog ? dog.name : t('petFallback', { id })}
                                                            </span>
                                                            {dog && (
                                                                <span className="item-details">
                                                                    {dog.breed && <span>{dog.breed}</span>}
                                                                    {dog.ownersName && <span>{dog.ownersName}</span>}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <button
                                                        className="modern-remove-btn"
                                                        onClick={() => setEditedDogIds(
                                                            editedDogIds.filter(dogId => dogId !== id)
                                                        )}
                                                    >
                                                        <FiX />
                                                    </button>
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <div className="modern-empty-edit-list">
                                        {t('noSelectedDogs')}
                                    </div>
                                )}
                            </div>
                        </>
                    )}

                    {/* TRAININGS SECTION - Always visible */}
                    <div className="modern-trainings-section">
                        <GroupTrainings groupId={groupId} />
                    </div>
                </div>

                {/* Modal for selecting trainers */}
                {isTrainerModalOpen && (
                    <div className="modern-modal-overlay">
                        <div className="modern-modal">
                            <div className="modern-modal-header">
                                <button
                                    className="modern-back-btn"
                                    onClick={() => setIsTrainerModalOpen(false)}
                                >
                                    <FiChevronLeft />
                                </button>
                                <h2 className="modern-modal-title">{t('selectTrainers')}</h2>
                                <button
                                    className="modern-close-btn"
                                    onClick={() => setIsTrainerModalOpen(false)}
                                >
                                    &times;
                                </button>
                            </div>

                            <div className="modern-modal-content">
                                <div className="modern-search-box">
                                    <FiSearch className="search-icon" />
                                    <input
                                        type="text"
                                        placeholder={t('searchTrainerPlaceholder')}
                                        value={modalTrainerSearch}
                                        onChange={(e) => setModalTrainerSearch(e.target.value)}
                                    />
                                </div>

                                <div className="modern-selection-list">
                                    {filteredModalTrainers.length === 0 ? (
                                        <p className="modern-empty-selection">{t('noTrainers')}</p>
                                    ) : (
                                        filteredModalTrainers.map((t) => (
                                            <div key={t.id} className="modern-selection-item">
                                                <label className="modern-checkbox">
                                                    <input
                                                        type="checkbox"
                                                        checked={editedTrainerIds.includes(t.id)}
                                                        onChange={(e) => {
                                                            if (e.target.checked) {
                                                                setEditedTrainerIds([...editedTrainerIds, t.id]);
                                                            } else {
                                                                setEditedTrainerIds(
                                                                    editedTrainerIds.filter((id) => id !== t.id)
                                                                );
                                                            }
                                                        }}
                                                    />
                                                    <span className="checkmark"></span>
                                                    <span className="name">{t.firstName} {t.lastName}</span>
                                                </label>
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>

                            <div className="modern-modal-footer">
                                <button
                                    className="modern-save-btn"
                                    onClick={() => setIsTrainerModalOpen(false)}
                                >
                                    {t('confirmSelection')}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Modal for selecting dogs */}
                {isDogModalOpen && (
                    <div className="modern-modal-overlay">
                        <div className="modern-modal">
                            <div className="modern-modal-header">
                                <button
                                    className="modern-back-btn"
                                    onClick={() => setIsDogModalOpen(false)}
                                >
                                    <FiChevronLeft />
                                </button>
                                <h2 className="modern-modal-title">{t('selectPets')}</h2>
                                <button
                                    className="modern-close-btn"
                                    onClick={() => setIsDogModalOpen(false)}
                                >
                                    &times;
                                </button>
                            </div>

                            <div className="modern-modal-content">
                                <div className="modern-search-box">
                                    <FiSearch className="search-icon" />
                                    <input
                                        type="text"
                                        placeholder={t('searchPetPlaceholder')}
                                        value={modalDogSearch}
                                        onChange={(e) => setModalDogSearch(e.target.value)}
                                    />
                                </div>

                                <div className="modern-selection-list">
                                    {filteredModalDogs.length === 0 ? (
                                        <p className="modern-empty-selection">{t('noPets')}</p>
                                    ) : (
                                        filteredModalDogs.map((d) => (
                                            <div key={d.id} className="modern-selection-item">
                                                <label className="modern-checkbox">
                                                    <input
                                                        type="checkbox"
                                                        checked={editedDogIds.includes(d.id)}
                                                        onChange={(e) => {
                                                            if (e.target.checked) {
                                                                setEditedDogIds([...editedDogIds, d.id]);
                                                            } else {
                                                                setEditedDogIds(
                                                                    editedDogIds.filter((id) => id !== d.id)
                                                                );
                                                            }
                                                        }}
                                                    />
                                                    <span className="checkmark"></span>
                                                    <span className="name">
                                                        {d.name} {d.breed && `(${d.breed})`}
                                                        {d.ownersName && <span className="owner"> - {d.ownersName}</span>}
                                                    </span>
                                                </label>
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>

                            <div className="modern-modal-footer">
                                <button
                                    className="modern-save-btn"
                                    onClick={() => setIsDogModalOpen(false)}
                                >
                                    {t('confirmSelection')}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Delete Confirmation Modal */}
                {isDeleteModalOpen && (
                    <DeleteConfirmationModal
                        isOpen={isDeleteModalOpen}
                        onClose={() => {
                            setIsDeleteModalOpen(false);
                        }}
                        onConfirm={handleDeleteGroup}
                        itemName={t('groupLabel', { name: groupDetails.name })}
                        isLoading={isDeleting}
                    />
                )}
            </div>
        </TrainerLayout>
    );
};

export default TrainerGroupDetails;